import React, { useState } from 'react';
import { string, object, bool } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { isUserBlocked } from './userBlockingUtils';
import css from './BlockUserButton.module.css';

/**
 * BlockUserButton — allows a user to block/unblock another user.
 *
 * Blocked user IDs are stored in currentUser.attributes.protectedData.blockedUsers[].
 * Blocking is done via the Sharetribe current user update API.
 *
 * Usage (in user profile page, message thread, etc.):
 *   <BlockUserButton
 *     targetUser={profileUser}
 *     currentUser={currentUser}
 *     onBlock={() => dispatch(fetchCurrentUser())}
 *   />
 *
 * @param {Object} props.targetUser — the user to block/unblock
 * @param {Object} props.currentUser — the logged-in user
 * @param {Function} [props.onBlock] — callback after block/unblock
 */
const BlockUserButton = ({ targetUser, currentUser, onBlock, className, showConfirm }) => {
  const targetId = targetUser?.id?.uuid;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const blocked = currentUser ? isUserBlocked(currentUser, targetId) : false;

  if (!currentUser || !targetId) return null;
  // Don't show block button for own profile
  if (currentUser?.id?.uuid === targetId) return null;

  const handleToggle = async () => {
    if (!window.confirm(
      blocked
        ? 'Unblock this user?'
        : 'Block this user? They will no longer be able to contact you.'
    )) return;

    setIsLoading(true);
    setError(null);

    const blockedUsers = currentUser?.attributes?.protectedData?.blockedUsers || [];
    const updated = blocked
      ? blockedUsers.filter(id => id !== targetId)
      : [...blockedUsers, targetId];

    try {
      const res = await fetch('/api/auth/update-profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ protectedData: { blockedUsers: updated } }),
      });
      if (!res.ok) throw new Error('Update failed');
      if (onBlock) onBlock({ blocked: !blocked, userId: targetId });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={classNames(css.root, className)}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={isLoading}
        className={classNames(css.button, blocked && css.unblockButton)}
      >
        {isLoading
          ? <FormattedMessage id="BlockUserButton.loading" />
          : blocked
          ? <FormattedMessage id="BlockUserButton.unblock" />
          : <FormattedMessage id="BlockUserButton.block" />
        }
      </button>
      {error && <p className={css.error}>{error}</p>}
    </div>
  );
};

BlockUserButton.defaultProps = {
  className: null,
  onBlock: null,
  showConfirm: true,
};

BlockUserButton.propTypes = {
  targetUser: object.isRequired,
  currentUser: object,
  className: string,
  onBlock: null,
  showConfirm: bool,
};

export default BlockUserButton;
